import { IconButton, SxProps, Theme } from '@mui/material'
import { FC, MouseEvent, ReactNode } from 'react'

import { CORAL_600, GRAY_200, GRAY_800, WHITE } from 'constants/styling/theme'

export type IconButtonVariant = 'outlined' | 'filled' | 'plain'
export type IconButtonSize = 'sm' | 'md' | 'lg'

interface Props {
  /** The glyph; its size follows `size`. */
  icon: ReactNode
  /** Says what the button does — there is no visible label. */
  ariaLabel: string
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  /** `outlined`: round, white, bordered (dismiss); `filled`: round and white over media; `plain`: the glyph alone (remove a row). */
  variant?: IconButtonVariant
  size?: IconButtonSize
  disabled?: boolean
  /** Colours the glyph as a destructive action, e.g. while a delete awaits confirmation. */
  danger?: boolean
  /** Placement only, e.g. absolute positioning over an image. */
  sx?: SxProps<Theme>
  'data-cy'?: string
}

const ICON_SIZE: Record<IconButtonSize, string> = { sm: '1.6rem', md: '2rem', lg: '2.4rem' }
const PADDING: Record<IconButtonSize, string> = { sm: '0.4rem', md: '0.8rem', lg: '0.3rem' }

/**
 * @component MUIIconButton
 * A button showing only an icon: closing a panel, removing a row, stepping through images.
 *
 * @example
 * <MUIIconButton icon={<CloseRoundedIcon />} ariaLabel={t('close')} onClick={onClose} />
 */
export const MUIIconButton: FC<Props> = ({
  icon,
  ariaLabel,
  onClick,
  variant = 'outlined',
  size = 'md',
  disabled = false,
  danger = false,
  sx,
  'data-cy': dataCy,
}) => (
  <IconButton
    aria-label={ariaLabel}
    onClick={onClick}
    disabled={disabled}
    data-cy={dataCy}
    sx={[
      {
        padding: PADDING[size],
        color: danger ? CORAL_600 : GRAY_800,
        '& svg': { fontSize: ICON_SIZE[size] },
        ...(variant !== 'plain' && {
          borderRadius: '50%',
          backgroundColor: WHITE,
          '&:hover': { backgroundColor: WHITE },
        }),
        ...(variant === 'outlined' && { border: `1px solid ${GRAY_200}` }),
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {icon}
  </IconButton>
)
